<!-- 
  Este arquivo é um exemplo de form de algum model.
  O form é utilizado para representar todos os campos necessário para um model.
  Ele é utilizado no new, no edit e no show. Por isso, é importante considerar isso quando estiver desenvolvendo.
-->
<template>
  <!-- 
    O form não contém nenhum componente base, porque ele é basicamente os campos que vão ser utilizados nas telas.
    Tudo que for implementado no form, ou vem do new e edit ou vai para o new e o edit. Ou seja, o form por si só não funciona sozinho.
  -->
  <v-form ref="form" class="">

    <BaseUtil
      ref="util"
      :pageLoading="pageLoading"
    />

    <v-row>

      <v-col cols="12" sm="8">
        <v-text-field
          v-if="($can('create', 'UserForAdmin') && (entry.id ? false : true))"
          v-model="entry.personAttributes.name"
          v-bind="getObject('personAttributes.name')"
          label="Nome"
          :disabled="(entry.id ? readOnly : false)"
          autocomplete="off"
        /> 

        <v-autocomplete
          v-else
          v-model="entry.personId"
          v-bind="getObject('personId')"
          label="Pessoa"
          :items="extraData.accountContext.allUsersCandidate"
          item-value="id"
          item-text="name"
          :disabled="(entry.id ? readOnly : false)"
          autocomplete="off"
        >
          <template v-slot:append-outer>
            <PersonNew
              :inlineAddButton="true"
              :isCollaborator="true"              
              @entry-created="personCreated"
              modelNameSingular="Pessoa"
            />
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="4">
        <v-switch 
          v-model="entry.isActive" 
          inset 
          :label="entry.isActive ? 'Ativo' : 'Inativo'"
          :disabled="!userIsAdminOrOwner || readOnly">
        </v-switch>
      </v-col>

      <!-- <v-col cols="12" sm="6">
        <v-file-input label="Selecionar logo" variant="underlined" v-model="file"></v-file-input>
      </v-col> -->

      <v-col cols="12"
        v-if="$can('create', 'UserForAdmin')"
      >
        <v-autocomplete
          v-model="entry.accountId"
          v-bind="getObject('accountId')"
          label="Conta"
          :items="extraData.accountContext.allAccounts"
          item-value="id"
          item-text="name"
          :disabled="(entry.id ? readOnly : false)"
          autocomplete="off"
        />
      </v-col>

      <v-col cols="12" sm="12" md="6">
        <v-text-field
          ref="emailField"
          v-model="entry.email"
          v-bind="getObject('email')"
          label="E-mail"
          autocomplete="new-password"
          :disabled="(entry.id ? readOnly : false)"
          inputmode="email"
          @blur="verifyEmail"
          :loading="emailVerificationLoading"
          :hint="emailVerificationLoading ? 'Verificando se este e-mail já está cadastrado no sistema...' : ''"
        />
      </v-col>

      <template v-if="!isEmailAlreadyRegistered && allowPasswordEditing">
        <v-col cols="12" sm="9" md="6" offset-sm="3" offset-md="0">
          <v-text-field
            v-model="entry.password"
            v-bind="getObject('password')"
            :append-icon="passwordShow ? 'mdi-eye' : 'mdi-eye-off'"
            :type="passwordShow ? 'text' : 'password'"
            :label="isEdit ? 'Nova Senha' : 'Senha'"
            hint="Use uma senha com no mínimo 6 digitos"
            counter
            @click:append="passwordShow = !passwordShow"
            autocomplete="new-password"
          ></v-text-field>
        </v-col>

        <v-col cols="12" sm="9" md="6" offset-sm="3" offset-md="6" >
          <v-text-field
            v-model="passwordConfirmation"
            :rules="passwordConfirmationRules"
            :append-icon="passwordShow ? 'mdi-eye' : 'mdi-eye-off'"
            :type="passwordShow ? 'text' : 'password'"
            :label="isEdit ? 'Confirmação de Nova Senha' : 'Confirmação de Senha'"
            counter
            @click:append="passwordShow = !passwordShow"
            autocomplete="new-password"
          ></v-text-field>
        </v-col>
      </template>

      <template v-else>
        <v-col cols="12" v-if="!allowPasswordEditing">
          <v-alert type="info">
            Apenas o próprio usuário (logado) pode realizar a alteração de senha!
          </v-alert>
        </v-col>
        <v-col cols="12" v-else>
          <v-alert type="info">
            Este e-mail já está cadastrado como um usuário AgroSim. 
            Desta forma, não é necessário informar a senha. 
            O usuário existente será automaticamente linkado à sua conta.
          </v-alert>
        </v-col>
      </template>

      <v-col cols="12">
        <v-autocomplete
          v-if="canEditUserProfile"
          v-model="entry.roleId"
          v-bind="getObject('roleId')"
          label="Perfil"
          :items="extraData.globalContext.allRoles"
          item-value="id"
          item-text="text"
          :disabled="partialReadOnly || readOnly"
          autocomplete="off"
        />
      </v-col>

      <v-col cols="12" sm="4" v-if="entry.roleId && entry.roleId == '9'">
        <v-switch 
          v-model="entry.personAttributes.isSupervisor" 
          inset
          :label="'Supervisor'"
          :disabled="!userIsAdminOrOwner || readOnly">
        </v-switch>
      </v-col>

      <v-col cols="12" v-if="entry.personAttributes.isSupervisor">
        <MultiSelect
          v-model="entry.personAttributes.subordinateIds"
          v-bind="getObject('personAttributes.subordinateIds')"
          label="Subordinados"
          :items="extraData.accountContext.allPosibleSubordinates"
          :loadingItems="loadingSubordinates"
          itemSubText="additionalInfo"
          itemSecondSubText="secondAdditionalInfo"
          :readOnly="readOnly"
        >
        </MultiSelect>
      </v-col>

      <v-col cols="12" class="" v-if="canEditUserProfile">
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header class="text-h6">
              Descrição dos tipos de perfís de usuários
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              
              <v-card
                v-for="(permission, index) in extraData.globalContext.allRoles"
                :key="index+'permission'"
                outlined
              >
                <v-card-title class="text-h7">
                  {{permission.text}}
                </v-card-title>

                <v-card-text>
                  {{permission.description}}
                </v-card-text>
              </v-card>

            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        
      </v-col>

    </v-row>
  </v-form>
</template>

<script>
// importação de cada um dos componentes adicionais utilizados
// todos os componentes que não forem padrões do vuetify precisa ser importados aqui
import MultiSelect from "@shared_components/multi_select.vue";
import PersonNew from "@models/people/new.vue";
import BaseUtil from "@shared_components/base_util.vue"
import gql from 'graphql-tag';

export default {
  data() {
    return {
      entryAttributes: {}, 
      passwordShow: false,
      passwordConfirmation: null,
      passwordConfirmationRules: [],
      isEmailAlreadyRegistered: false,
      emailVerificationLoading: false,
      pageLoading: false,
      loadingSubordinates: false,
      file: null,
    };
  },
  props: {
    entry: Object, //objeto que vai conter todas as informações do form - objeto principal
    extraData: Object, // dados extras necessarios para 
    readOnly: { //campo que define o estado do form, no new e edit o readOnly é false, no show o readOnly é true
      type: Boolean,
      default: false //por default é falso
    },
    partialReadOnly: { // utilizado para alguns campos que fogem do padrão do readOnly
      type: Boolean,
      default: false
    },
    allowPasswordEditing: {
      type: Boolean,
      default: true
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    modelNameSingular: String,
  },
  //importação dos componentes adicionais
  components: {
    MultiSelect: MultiSelect,
    PersonNew: PersonNew,
    BaseUtil: BaseUtil
    // MaskedTextField: MaskedTextField,
    // DateField: DateField,
    // PaymentIndex: PaymentIndex,
    // ChartOfAccountSelect: ChartOfAccountSelect
  },
  //funcao executada quando o form é criado
  created() {
    this.entryAttributes = this.setupEntryAttributes(this.entry);
    // console.log("entryAttributes", this.entryAttributes);
    this.passwordConfirmationRules = [];
    for (let i = 0; i < this.entryAttributes.password.rules.length; i++) {
      this.passwordConfirmationRules.push(this.entryAttributes.password.rules[i]);  
    }
    this.passwordConfirmationRules.push(this.passwordConfirmationRule);
    this.entryAttributes.email.rules.push(this.emailRule);
  },
  //funcao executada quando o form é mounted
  mounted() {
  },
  watch: {
    "file": function() {
      // console.log("Alterou entry.periodIdsAttribute", this.entry.periodIdsAttribute)
      // console.log(this.file);
      var reader = new FileReader();
      var self = this;
      // console.log("self", self);
      reader.readAsDataURL(this.file);
      reader.onload = function(){
        // console.log(reader.result);
        // console.log("selfentry", self.entry);
        self.entry.file = reader.result;
      };
    },
  },
  // funções computadas
  computed: {
    passwordConfirmationRule() {
      return () => (this.passwordConfirmation === this.entry.password) || 'As senhas inseridas não combinam! A confirmação de senha deve ser igual a senha.'
    },
    canEditUserProfile() {
      return (!this.$can('create', 'UserForAdmin') || (this.entry.id ? true : false)) && this.userIsAdminOrOwner
    },
    userIsAdminOrOwner() {
      return (this.$can('create', 'UserForAdmin') || this.$can('create', 'User')) 
    }
  },
  //declaração das funções
  methods: {
    // todo campo exibido no form utiliza esse método para pegar as meta-info do campo, como validações 
    getObject: function(element, component) {
      return this.entryAttributes[element];
    },
    //verifica se o form está valido, caso não estiver, já escrola a modal para o campo que deu erro
    isFormValid: function() {
      if (this.$refs.form.validate()) {
        return true;
      } else {
        setTimeout(function(){
          document.querySelector(".v-messages.error--text:first-of-type").scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500);
      }
      return false;
    },
    // reseta o form, limpando os campos e tirando as validações
    resetFormInputs: function() {
      this.$refs.form.resetValidation();
    },
    personCreated: function(person) {
      //testar pra ver se o tipo é supplier
      if (person.isCollaborator || person.isEmployee) {
        this.extraData.accountContext.allUsersCandidate.unshift(person);
        this.entry.personId = person.id;
        this.entry.person = person;
      } else {
        this.$refs.util.showErrorMessage("A pessoa cadastrada não é do tipo Colaborador ou Funcionário, por isso não irá aparecer na lista de pessoas!");
      }
    },
    verifyEmail: function() {
      // console.log("email blur: ", this.entry.email);
      // console.log("ref", this.$refs, this.$refs.emailField);
      if (this.entry.email && this.$refs.emailField.valid){
        this.emailVerificationLoading = true;
        this.$apollo
          .mutate({
            mutation: VERIFY_EMAIL_AVAILABILITY,
            variables: {
              email: this.entry.email,
            }
          })
          .then(response => {
            // console.log(response);
            this.isEmailAlreadyRegistered = response.data.accountContext.isEmailAlreadyRegistered
          })
          .catch(response => console.log(response))
          .finally(() => this.pageLoading = this.emailVerificationLoading = false);
      }
    }
  }
};


const VERIFY_EMAIL_AVAILABILITY = gql `
  query ($email: String!) {
    accountContext {
      isEmailAlreadyRegistered(email: $email) 
    }
  }
`;

</script>

<style scoped>
  /* Exemplo de classe css utilizada em campos */
  /* ::v-deep .enabledTextFieldChartOfAccount input {
    cursor: pointer;
  } */
</style>
